// Sensores: distancia, temperatura y humedad, movimiento, luz, sonido, gas, agua, inclinación,
// campo magnético y 1-Wire. Las constantes de geometría (mm) y las curvas de cada sensor las
// comparten la definición (pines), la simulación y el modelo 3D.
import { definePart, PITCH } from './registry.js';

// Utilidades -----------------------------------------------------------------------------------------
export function clamp(v, a, b) {
  v = Number(v);
  if (!Number.isFinite(v)) return a;
  return Math.min(b, Math.max(a, v));
}

function fmt(n, dec = 0) {
  return Number(n).toLocaleString('es-MX', { minimumFractionDigits: dec, maximumFractionDigits: dec });
}

/** Redondea a 2–3 cifras significativas para mostrar valores de escala amplia. */
function sig(n) {
  if (n >= 1000) return fmt(Math.round(n / 10) * 10);
  if (n >= 100) return fmt(Math.round(n));
  if (n >= 10) return fmt(n, 0);
  return fmt(n, 1);
}

/** Fila de pines insertables centrada en x = 0 (patas hacia abajo, en y = 0). */
function legs(names, labels, kinds) {
  return names.map((name, i) => ({
    name,
    label: labels[name] || name,
    kind: kinds[name] || 'signal',
    x: (i - (names.length - 1) / 2) * PITCH,
    y: 0,
    z: 0,
    insert: true,
  }));
}

/** Conector macho en ángulo recto en el borde izquierdo: pines en columna (eje Z) hacia −X. */
function sideHeader(names, labels, kinds, { x, y }) {
  return names.map((name, i) => ({
    name,
    label: labels[name] || name,
    kind: kinds[name] || 'signal',
    x,
    y,
    z: (i - (names.length - 1) / 2) * PITCH,
    dir: [-1, 0, 0],
  }));
}

/** Umbral del comparador LM393: posición de la perilla → voltaje de referencia. */
const thresholdProp = (def = 0.5) => ({
  key: 'threshold',
  label: 'Umbral de DO (perilla azul)',
  type: 'slider',
  min: 0,
  max: 1,
  step: 0.001,
  default: def,
  runtime: true,
  live: true,
  format: (v) => `${fmt(v * 5, 2)} V · ${Math.round(v * 1023)}`,
});

// Curvas de los sensores ------------------------------------------------------------------------------

/** Iluminación (lux) a partir de la posición logarítmica 0–1: 0.1 lx a 100 000 lx. */
export function luxOf(v) {
  return Math.pow(10, -1 + 6 * clamp(v, 0, 1));
}

export function formatLux(lux) {
  const where =
    lux < 1 ? 'noche' : lux < 20 ? 'penumbra' : lux < 200 ? 'interior' : lux < 1000 ? 'oficina' : lux < 10000 ? 'día nublado' : 'sol directo';
  return `${sig(lux)} lx · ${where}`;
}

/** Fotorresistencia (CdS): R = R10·(lux/10)^−γ, con tope de oscuridad. */
export const LDR = { r10: 50000, gamma: 0.7, dark: 1e6 };
export function ldrOhms(lux) {
  return Math.min(LDR.dark, LDR.r10 * Math.pow(Math.max(lux, 1e-3) / 10, -LDR.gamma));
}

/** Termistor NTC 10 kΩ a 25 °C con B = 3950. */
export const NTC = { r25: 10000, beta: 3950 };
export function ntcOhms(celsius) {
  return NTC.r25 * Math.exp(NTC.beta * (1 / (celsius + 273.15) - 1 / 298.15));
}

/** MQ-2: concentración (ppm) a partir de la posición logarítmica 0–1 (0 a 10 000 ppm). */
export function ppmOf(v) {
  return Math.pow(10, 4 * clamp(v, 0, 1)) - 1;
}

/** MQ-2: Rs/R0 según la curva del fabricante para GLP (≈ 1.7 a 200 ppm, ≈ 0.27 a 10 000 ppm). */
export const MQ2 = { r0: 10000, rl: 10000, cleanAir: 9.83 };
export function mq2Ratio(ppm) {
  if (ppm <= 0) return MQ2.cleanAir;
  return Math.min(MQ2.cleanAir, 20.5 * Math.pow(ppm, -0.47));
}

/** PIR: posición de la perilla Tx → retardo de 3 s a 300 s (escala logarítmica). */
export function pirDelayOf(v) {
  return 3 * Math.pow(100, clamp(v, 0, 1));
}

// Calentamiento -----------------------------------------------------------------------------------------

/** Escala de tiempo del calentamiento: real, rápido (÷10, para no esperar minutos) o apagado (listo al instante). */
export const WARMUP_SCALE = { real: 1, fast: 0.1, off: 0 };
export const warmupScale = (mode) => (mode in WARMUP_SCALE ? WARMUP_SCALE[mode] : WARMUP_SCALE.fast);

const warmupProp = (real, fast, what) => ({
  key: 'warmup',
  label: `Calentamiento (${what})`,
  type: 'select',
  default: 'fast',
  runtime: true,
  options: [{ value: 'real', label: `Real (${real})` }, { value: 'fast', label: `Rápido (${fast})` }, { value: 'off', label: 'Apagado' }],
});

/**
 * PIR HC-SR501: tras recibir alimentación el módulo tarda ~1 min en inicializarse y, mientras tanto, su salida
 * puede activarse de 0 a 3 veces (hoja de datos del HC-SR501: «after a minute… will output 0-3 times»).
 */
export const PIR_INIT_S = 60;

/**
 * MQ-2 recién energizado: el elemento de SnO₂ está frío y Rs arranca muy por debajo de su valor estable (AO alto);
 * Rs se recupera en minutos. La hoja de Winsen (v1.5) pide ≥ 48 h de precalentamiento para lecturas calibradas pero
 * no da la curva inicial: la forma es la típica de estos sensores y los valores son representativos.
 * Rs sigue además los cambios de gas con una respuesta de primer orden: la gráfica «Response and Resume» de la hoja
 * sube en unos 5 s y baja en ~10 s con una cola de ~30 s; con τ = 2.5 s al subir y 8 s al bajar el 95 % llega en
 * menos de 10 s y de 30 s.
 *   start: fracción de Rs estable al encender · tau: constante del calentamiento (s) ·
 *   resp / rec: constantes de la respuesta y de la recuperación ante el gas (s) ·
 *   settled: avance (en tau) con el que Rs ya está al 95 % de su valor estable.
 */
export const MQ2_HEAT = { start: 0.03, tau: 40, resp: 2.5, rec: 8, settled: 4.23 };

function formatSeconds(s) {
  if (s < 10) return `${fmt(s, 1)} s`;
  if (s < 60) return `${Math.round(s)} s`;
  const m = Math.floor(Math.round(s) / 60);
  const r = Math.round(s) % 60;
  return r ? `${m} min ${r} s` : `${m} min`;
}

// Geometría compartida ------------------------------------------------------------------------------

export const HCSR04 = {
  w: 45, h: 20, t: 1.6,
  pcbY: 1.4,          // borde inferior del PCB
  back: 1.25,         // cara trasera del PCB (z); los pines quedan en z = 0
  tx: 13,             // centro de cada transductor (±x)
  r: 8,               // radio de los transductores
  len: 12,            // salen 12 mm al frente
  pinY: 3,            // altura de los pines en ángulo recto que atraviesan el PCB
};
HCSR04.front = HCSR04.back + HCSR04.t;
HCSR04.cy = HCSR04.pcbY + HCSR04.h / 2;

export const DHT = {
  dht22: { w: 15.1, h: 25, d: 7.7, y0: 2.2, color: '#f2f1ec' },
  dht11: { w: 12, h: 15.5, d: 5.5, y0: 2.2, color: '#3a7bd5' },
};

export const PIR = {
  w: 32.3, d: 24.3, t: 1.2,
  pcbY: 7.6,          // cara inferior del PCB (los componentes cuelgan debajo)
  zc: -10.15,         // centro del PCB: el conector está en el borde delantero (z = 0)
  lens: 23.6,         // marco cuadrado de la lente
  dome: 11.3,         // radio de la cúpula Fresnel
};
PIR.top = PIR.pcbY + PIR.t;

export const TO92 = { y0: 4, h: 5, r: 2.4, zc: -0.4, flat: 1.0 };

/** Placas tipo KY (18.5 × 15 mm) con conector de 3 pines en ángulo recto. */
export const KY = { w: 18.5, d: 15, t: 1.6, y0: 0.8, pinLen: 6 };
KY.top = KY.y0 + KY.t;
KY.pinY = KY.top + 1.27;
KY.pinX = -KY.w / 2 - KY.pinLen;

/** Módulos con comparador LM393 (placa de 32 × 14 mm, conector de 4 pines a la izquierda). */
export const LM393 = { w: 32, d: 14, t: 1.6, y0: 0.8, pinLen: 6 };
LM393.top = LM393.y0 + LM393.t;
LM393.pinY = LM393.top + 1.27;
LM393.pinX = -LM393.w / 2 - LM393.pinLen;

export const KY038 = { w: 44, d: 15, t: 1.6, y0: 0.8, pinLen: 6 };
KY038.top = KY038.y0 + KY038.t;
KY038.pinY = KY038.top + 1.27;
KY038.pinX = -KY038.w / 2 - KY038.pinLen;

export const MQ2BOARD = { w: 32, d: 26, t: 1.6, pcbY: 6, zc: -11, sensorZ: -12.6 };
MQ2BOARD.top = MQ2BOARD.pcbY + MQ2BOARD.t;

/** Sensor de humedad de suelo: comparador (30 × 16) y horquilla (60 × 20) unidos por 2 cables. */
export const SOIL = { w: 30, d: 16, t: 1.6, y0: 0.8, pinLen: 6, probeX0: 62, probeW: 60, probeD: 20, prongs: 38 };
SOIL.top = SOIL.y0 + SOIL.t;
SOIL.pinY = SOIL.top + 1.27;
SOIL.pinX = -SOIL.w / 2 - SOIL.pinLen;

export const WATER = { w: 62, d: 20, t: 1.6, y0: 0.8, pinLen: 6, traceX0: -12, traceLen: 40, maxLevel: 40 };
WATER.top = WATER.y0 + WATER.t;
WATER.pinY = WATER.top + 1.27;
WATER.pinX = -WATER.w / 2 - WATER.pinLen;

// Distancia ------------------------------------------------------------------------------------------
definePart({
  type: 'hc-sr04',
  name: 'Sensor ultrasónico HC-SR04',
  category: 'sensores',
  description: 'Mide distancias de 2 a 400 cm: un pulso de 10 µs en TRIG dispara 8 ciclos de 40 kHz y ECHO queda en alto el tiempo de ida y vuelta del eco (58 µs por cm).',
  keywords: 'ultrasonico ultrasonido sonar distancia hc-sr04 hcsr04 newping pulsein eco proximidad',
  insertable: true,
  props: [
    { key: 'distance', label: 'Distancia al obstáculo', type: 'slider', min: 2, max: 400, step: 0.5, unit: 'cm', default: 30, runtime: true, live: true },
    { key: 'noEcho', label: 'Nada enfrente (sin eco)', type: 'boolean', default: false, runtime: true },
  ],
  pins: legs(['VCC', 'TRIG', 'ECHO', 'GND'], { VCC: 'VCC (5 V)', TRIG: 'Trig (disparo)', ECHO: 'Echo (eco)', GND: 'GND' }, { VCC: 'power', TRIG: 'digital', ECHO: 'digital', GND: 'gnd' }),
  footprint: { w: 45.4, d: 16.4, h: 21.6, z: 6.8 },
  help: 'TRIG y ECHO van a dos pines digitales. Sin eco (nada enfrente) ECHO queda en alto unos 38 ms. ' +
    'Necesita 5 V. Deja unos 60 ms entre mediciones para que no lleguen ecos de la anterior. ' +
    'Con la librería NewPing también sirve un solo pin: une TRIG y ECHO (NewPing admite el cable directo; muchos montajes ' +
    'ponen una resistencia de 1 a 2.2 kΩ entre ellos) y declara NewPing sonar(pin, pin, máximo): el Arduino manda el pulso ' +
    'de 12 µs y luego cambia el pin a entrada para oír el eco.',
});

// Temperatura y humedad -------------------------------------------------------------------------------
const dhtPins = legs(['VCC', 'SDA', 'NC', 'GND'], { VCC: '1 · VCC (3.3–5 V)', SDA: '2 · DATA (datos)', NC: '3 · NC (sin conexión)', GND: '4 · GND' }, { VCC: 'power', SDA: 'digital', NC: 'signal', GND: 'gnd' });

definePart({
  type: 'dht22',
  name: 'Sensor de temperatura y humedad DHT22',
  category: 'sensores',
  description: 'AM2302: temperatura de −40 a 80 °C (0.1 °C) y humedad de 0 a 100 % por un solo cable de datos (librería DHT, tipo DHT22).',
  keywords: 'dht22 am2302 temperatura humedad higrometro termometro dht',
  insertable: true,
  props: [
    { key: 'temperature', label: 'Temperatura', type: 'slider', min: -40, max: 80, step: 0.1, unit: '°C', default: 24, runtime: true, live: true },
    { key: 'humidity', label: 'Humedad relativa', type: 'slider', min: 0, max: 100, step: 0.1, unit: '%', default: 55, runtime: true, live: true },
  ],
  pins: dhtPins,
  footprint: { w: 15.6, d: 8.2, h: 27.4 },
  help: 'La librería DHT activa el pull-up interno del pin; con cables largos agrega 10 kΩ entre DATA y VCC. Lee como máximo cada 2 s.',
});

definePart({
  type: 'dht11',
  name: 'Sensor de temperatura y humedad DHT11',
  category: 'sensores',
  description: 'Versión económica: temperatura de 0 a 50 °C y humedad de 20 a 90 % en pasos de 1, por un solo cable (librería DHT, tipo DHT11).',
  keywords: 'dht11 temperatura humedad higrometro termometro dht',
  insertable: true,
  props: [
    { key: 'temperature', label: 'Temperatura', type: 'slider', min: 0, max: 50, step: 1, unit: '°C', default: 24, runtime: true, live: true },
    { key: 'humidity', label: 'Humedad relativa', type: 'slider', min: 20, max: 90, step: 1, unit: '%', default: 55, runtime: true, live: true },
  ],
  pins: dhtPins,
  footprint: { w: 12.6, d: 6, h: 17.9 },
  help: 'Necesita que el Arduino baje la línea al menos 18 ms para responder (la librería DHT lo hace si declaras el tipo DHT11). Lee como máximo una vez por segundo.',
});

// Movimiento -----------------------------------------------------------------------------------------
definePart({
  type: 'pir',
  name: 'Sensor de movimiento PIR HC-SR501',
  category: 'sensores',
  description: 'Detecta el calor de una persona en movimiento: OUT sube a 3.3 V mientras hay movimiento y durante el retardo ajustado con la perilla Tx.',
  keywords: 'pir hc-sr501 movimiento presencia infrarrojo alarma sensor',
  insertable: true,
  props: [
    { key: 'motion', label: 'Movimiento (mantener presionado)', type: 'button' },
    { key: 'delay', label: 'Retardo (perilla Tx)', type: 'slider', min: 0, max: 1, step: 0.001, default: 0.111, runtime: true, live: true, format: (v) => formatSeconds(pirDelayOf(v)) },
    { key: 'mode', label: 'Modo (puente)', type: 'select', default: 'repeat', runtime: true,
      options: [{ value: 'repeat', label: 'H · repetible' }, { value: 'single', label: 'L · un disparo' }] },
    warmupProp('≈ 1 min', '≈ 6 s', 'inicialización'),
  ],
  pins: legs(['VCC', 'OUT', 'GND'], { VCC: 'VCC (5–20 V)', OUT: 'OUT (3.3 V con movimiento)', GND: 'GND' }, { VCC: 'power', OUT: 'digital', GND: 'gnd' }),
  footprint: { w: 32.5, d: 24.5, h: 23, z: PIR.zc },
  status: (p, v) => (v.on && v.init ? `Inicializando: quedan ${formatSeconds(v.init)}. No detecta el movimiento y su salida puede activarse sola (0 a 3 veces).` : null),
  help: 'Mantén presionado el domo blanco (o el botón de arriba) para simular movimiento. ' +
    'En modo H el retardo vuelve a empezar mientras siga el movimiento; en modo L la salida dura solo el retardo. ' +
    'Al apagarse la salida, el sensor ignora el movimiento unos 2.5 s. ' +
    'Como el real, al recibir alimentación se inicializa durante ~1 min: no detecta el movimiento y su salida puede activarse sola ' +
    '0 a 3 veces (el módulo real no tiene ningún indicador de esto). «Rápido» lo reduce a ~6 s y «Apagado» lo salta.',
});

// Luz ------------------------------------------------------------------------------------------------
const lightProp = {
  key: 'light', label: 'Iluminación', type: 'slider', min: 0, max: 1, step: 0.001, default: 0.6, runtime: true, live: true,
  format: (v) => formatLux(luxOf(v)),
};

definePart({
  type: 'photoresistor',
  name: 'Fotorresistencia (LDR)',
  category: 'sensores',
  description: 'Resistencia que baja con la luz: unos 10 kΩ en interior y casi 1 MΩ a oscuras. Úsala en un divisor con una resistencia de 10 kΩ y lee el punto medio con analogRead.',
  keywords: 'ldr fotoresistencia fotorresistencia fotocelda luz gl5528 gl5537',
  insertable: true,
  props: [lightProp],
  pins: legs(['1', '2'], { 1: 'Terminal 1', 2: 'Terminal 2' }, {}),
  footprint: { w: 5.6, d: 5.6, h: 7.2 },
});

const moduleLabels = { VCC: 'VCC (3.3–5 V)', GND: 'GND', DO: 'DO (salida digital)', AO: 'AO (salida analógica)' };
const moduleKinds = { VCC: 'power', GND: 'gnd', DO: 'digital', AO: 'analog' };

definePart({
  type: 'photoresistor-module',
  name: 'Módulo de luz (LDR + LM393)',
  category: 'sensores',
  description: 'Fotorresistencia con comparador: AO baja con más luz y DO pasa a LOW (y enciende su LED) cuando la luz supera el umbral de la perilla.',
  keywords: 'modulo luz ldr fotoresistencia lm393 comparador sensor luminosidad',
  props: [lightProp, thresholdProp(0.5)],
  pins: sideHeader(['VCC', 'GND', 'DO', 'AO'], moduleLabels, moduleKinds, { x: LM393.pinX, y: LM393.pinY }),
  footprint: { w: LM393.w + 7, d: LM393.d, h: 9, x: -3.5 },
  help: 'AO es el divisor entre la LDR y 10 kΩ. DO es salida de colector abierto con pull-up de 10 kΩ: está en HIGH con poca luz y en LOW cuando AO baja del umbral.',
});

// Temperatura analógica -------------------------------------------------------------------------------
definePart({
  type: 'ntc',
  name: 'Termistor NTC 10 kΩ',
  category: 'sensores',
  description: 'Resistencia que baja al subir la temperatura: 10 kΩ a 25 °C con B = 3950. Arma un divisor con 10 kΩ y calcula la temperatura con la ecuación Beta.',
  keywords: 'ntc termistor thermistor temperatura 10k 3950 mf52',
  insertable: true,
  props: [{ key: 'temperature', label: 'Temperatura', type: 'slider', min: -40, max: 125, step: 0.5, unit: '°C', default: 25, runtime: true, live: true }],
  pins: legs(['1', '2'], { 1: 'Terminal 1', 2: 'Terminal 2' }, {}),
  footprint: { w: 4.6, d: 3.4, h: 8.6 },
});

definePart({
  type: 'ntc-module',
  name: 'Módulo termistor KY-013',
  category: 'sensores',
  description: 'Termistor NTC de 10 kΩ con su resistencia de 10 kΩ en una placa: S da un voltaje que sube con la temperatura.',
  keywords: 'ky-013 ky013 ntc termistor modulo temperatura analogico',
  props: [{ key: 'temperature', label: 'Temperatura', type: 'slider', min: -40, max: 125, step: 0.5, unit: '°C', default: 25, runtime: true, live: true }],
  pins: sideHeader(['S', 'VCC', 'GND'], { S: 'S (señal analógica)', VCC: '+ (VCC, pin central)', GND: '− (GND)' }, { S: 'analog', VCC: 'power', GND: 'gnd' }, { x: KY.pinX, y: KY.pinY }),
  footprint: { w: KY.w + 6, d: KY.d, h: 9, x: -3 },
  help: 'El NTC va entre + y S, y la resistencia fija de 10 kΩ entre S y −. Así: R<sub>NTC</sub> = 10 kΩ · (1023 / lectura − 1).',
});

definePart({
  type: 'lm35',
  name: 'Sensor de temperatura LM35',
  category: 'sensores',
  description: 'Salida lineal de 10 mV por °C (250 mV = 25 °C). Visto de frente (cara plana): +Vs, Vout y GND.',
  keywords: 'lm35 lm35dz temperatura termometro analogico to-92',
  insertable: true,
  props: [{ key: 'temperature', label: 'Temperatura', type: 'slider', min: 0, max: 150, step: 0.5, unit: '°C', default: 25, runtime: true, live: true }],
  pins: legs(['VS', 'VOUT', 'GND'], { VS: '+Vs (4–30 V)', VOUT: 'Vout (10 mV/°C)', GND: 'GND' }, { VS: 'power', VOUT: 'analog', GND: 'gnd' }),
  footprint: { w: 7.2, d: 4.6, h: 9.2, z: -0.4 },
  help: 'Temperatura = analogRead(pin) · 500 / 1024. Con alimentación sencilla mide de 2 °C a 150 °C.',
});

definePart({
  type: 'tmp36',
  name: 'Sensor de temperatura TMP36',
  category: 'sensores',
  description: 'Salida de 500 mV a 0 °C más 10 mV por °C (750 mV = 25 °C), de −40 a 125 °C. Visto de frente: +Vs, Vout y GND.',
  keywords: 'tmp36 temperatura termometro analogico to-92',
  insertable: true,
  props: [{ key: 'temperature', label: 'Temperatura', type: 'slider', min: -40, max: 125, step: 0.5, unit: '°C', default: 25, runtime: true, live: true }],
  pins: legs(['VS', 'VOUT', 'GND'], { VS: '+Vs (2.7–5.5 V)', VOUT: 'Vout (0.5 V + 10 mV/°C)', GND: 'GND' }, { VS: 'power', VOUT: 'analog', GND: 'gnd' }),
  footprint: { w: 7.2, d: 4.6, h: 9.2, z: -0.4 },
  help: 'Temperatura = (voltaje − 0.5) · 100, con voltaje = analogRead(pin) · 5.0 / 1024.',
});

// Módulos analógicos con comparador -----------------------------------------------------------------
definePart({
  type: 'sound-sensor',
  name: 'Sensor de sonido KY-038',
  category: 'sensores',
  description: 'Micrófono con comparador: AO es la señal del micrófono alrededor de VCC/2 y D0 da pulsos en alto cuando el sonido supera el umbral de la perilla.',
  keywords: 'sonido microfono ky-038 ky038 aplauso ruido clap lm393',
  props: [
    { key: 'level', label: 'Sonido ambiente', type: 'slider', min: 30, max: 110, step: 1, unit: 'dB', default: 40, runtime: true, live: true },
    { key: 'loud', label: 'Aplauso / ruido fuerte (mantener)', type: 'button' },
    thresholdProp(0.55),
  ],
  pins: sideHeader(['AO', 'GND', 'VCC', 'DO'], { AO: 'A0 (señal analógica)', GND: 'G (GND)', VCC: '+ (VCC)', DO: 'D0 (salida digital)' }, moduleKinds, { x: KY038.pinX, y: KY038.pinY }),
  footprint: { w: KY038.w + 7, d: KY038.d, h: 11, x: -3.5 },
  help: 'El sonido se simula como un tono de 1 kHz: AO oscila alrededor de 2.5 V con una amplitud que crece con los decibeles. ' +
    'Mantén presionado el micrófono para hacer ruido fuerte (≈ 100 dB). Ajusta la perilla justo arriba de 2.5 V para detectar aplausos.',
});

definePart({
  type: 'gas-sensor',
  name: 'Sensor de gas MQ-2',
  category: 'sensores',
  description: 'Detecta gas LP, butano, humo e hidrógeno: AO sube con la concentración y DO pasa a LOW cuando supera el umbral de la perilla.',
  keywords: 'mq-2 mq2 gas humo glp butano propano detector fuga',
  insertable: true,
  props: [
    { key: 'gas', label: 'Concentración de gas', type: 'slider', min: 0, max: 1, step: 0.001, default: 0, runtime: true, live: true,
      format: (v) => { const p = ppmOf(v); return p < 5 ? 'aire limpio' : `${sig(p)} ppm`; } },
    thresholdProp(0.4),
    warmupProp('≈ 3 min', '≈ 17 s', 'precalentamiento'),
  ],
  pins: legs(['VCC', 'GND', 'DO', 'AO'], moduleLabels, moduleKinds),
  footprint: { w: 32.5, d: 26.5, h: 24, z: MQ2BOARD.zc },
  status: (p, v) => (v.on && v.warm ? `Calentando: Rs todavía es baja (AO alto y DO puede dar LOW); se estabiliza en ~${formatSeconds(v.warm)}.` : null),
  help: 'El elemento sensor (Rs) forma un divisor con una resistencia de carga de 10 kΩ. El calefactor consume unos 150 mA: aliméntalo desde 5 V, no desde un pin. ' +
    'Al energizarlo el elemento está frío: Rs arranca baja (AO alto, y DO puede dar LOW) y tarda minutos en estabilizarse; además responde al gas ' +
    'sin prisa (unos 5 s al subir y 10–30 s al bajar). «Rápido» lo acelera 10 veces y «Apagado» da lecturas ideales al instante. ' +
    'La hoja de datos pide 48 h de precalentamiento para lecturas calibradas: eso no se simula.',
});

definePart({
  type: 'soil-moisture',
  name: 'Sensor de humedad de suelo',
  category: 'sensores',
  description: 'Horquilla que mide la resistencia de la tierra con un comparador LM393: AO es alto en seco y bajo en tierra mojada; DO pasa a LOW al superar la humedad del umbral.',
  keywords: 'humedad suelo tierra higrometro maceta riego fc-28 yl-69 yl-38 lm393',
  props: [
    { key: 'moisture', label: 'Humedad del suelo', type: 'slider', min: 0, max: 100, step: 1, unit: '%', default: 40, runtime: true, live: true },
    thresholdProp(0.5),
  ],
  pins: sideHeader(['VCC', 'GND', 'DO', 'AO'], moduleLabels, moduleKinds, { x: SOIL.pinX, y: SOIL.pinY }),
  footprint: { w: SOIL.probeX0 + SOIL.probeW - SOIL.pinX, d: SOIL.probeD, h: 6, x: (SOIL.probeX0 + SOIL.probeW + SOIL.pinX) / 2 },
  help: 'En aire AO marca casi 1023 y en agua unos 300. Para que la horquilla no se corroa, aliméntala desde un pin digital solo al medir.',
});

definePart({
  type: 'flame-sensor',
  name: 'Sensor de llama',
  category: 'sensores',
  description: 'Receptor infrarrojo (760–1100 nm) con comparador: AO baja cuanto más intensa o cercana es la llama y DO pasa a LOW al detectarla.',
  keywords: 'llama fuego flama infrarrojo ir incendio lm393 flame',
  props: [
    { key: 'flame', label: 'Llama (radiación infrarroja)', type: 'slider', min: 0, max: 1, step: 0.01, default: 0, runtime: true, live: true },
    thresholdProp(0.5),
  ],
  pins: sideHeader(['VCC', 'GND', 'DO', 'AO'], moduleLabels, moduleKinds, { x: LM393.pinX, y: LM393.pinY }),
  footprint: { w: LM393.w + 14, d: LM393.d, h: 9, x: 0 },
});

definePart({
  type: 'water-level',
  name: 'Sensor de nivel de agua',
  category: 'sensores',
  description: 'Pistas expuestas y un transistor: S da un voltaje que sube con la parte sumergida (0 V en seco, unos 3 V totalmente sumergido).',
  keywords: 'agua nivel lluvia water level liquido tanque',
  props: [{ key: 'level', label: 'Parte sumergida', type: 'slider', min: 0, max: WATER.maxLevel, step: 0.5, unit: 'mm', default: 0, runtime: true, live: true }],
  pins: sideHeader(['S', 'VCC', 'GND'], { S: 'S (señal analógica)', VCC: '+ (VCC)', GND: '− (GND)' }, { S: 'analog', VCC: 'power', GND: 'gnd' }, { x: WATER.pinX, y: WATER.pinY }),
  footprint: { w: WATER.w + 6, d: WATER.d, h: 6, x: -3 },
});

// Inclinación y campo magnético -----------------------------------------------------------------------
definePart({
  type: 'tilt-switch',
  name: 'Interruptor de inclinación SW-520D',
  category: 'sensores',
  description: 'Dos esferas metálicas cierran el contacto cuando está derecho; al inclinarlo más de unos 45° el circuito se abre.',
  keywords: 'inclinacion tilt sw-520d bola vibracion interruptor mercurio',
  insertable: true,
  props: [{ key: 'tilted', label: 'Inclinado', type: 'boolean', default: false, runtime: true }],
  pins: legs(['1', '2'], { 1: 'Terminal 1', 2: 'Terminal 2' }, {}),
  toggle: (p) => ({ key: 'tilted', value: !p.tilted }),
  footprint: { w: 6.4, d: 6.4, h: 14.5 },
  interactive: 'toggle',
  help: 'Haz clic en el sensor para inclinarlo o enderezarlo. Úsalo como un botón: un pin a GND y el otro a una entrada con INPUT_PULLUP.',
});

definePart({
  type: 'hall-sensor',
  name: 'Sensor de efecto Hall KY-003',
  category: 'sensores',
  description: 'Sensor A3144 con LED: S pasa a LOW cuando se acerca el polo de un imán y vuelve a HIGH al retirarlo.',
  keywords: 'hall ky-003 ky003 a3144 iman magnetico campo rpm',
  props: [{ key: 'magnet', label: 'Imán cerca', type: 'boolean', default: false, runtime: true }],
  pins: sideHeader(['S', 'VCC', 'GND'], { S: 'S (salida digital)', VCC: '+ (VCC, pin central)', GND: '− (GND)' }, { S: 'digital', VCC: 'power', GND: 'gnd' }, { x: KY.pinX, y: KY.pinY }),
  toggle: (p) => ({ key: 'magnet', value: !p.magnet }),
  footprint: { w: KY.w + 6, d: KY.d, h: 8, x: -3 },
  interactive: 'toggle',
  help: 'Haz clic en el sensor para acercar o retirar el imán. La placa trae la resistencia de pull-up de 10 kΩ en S.',
});

// 1-Wire --------------------------------------------------------------------------------------------
definePart({
  type: 'ds18b20',
  name: 'Sensor de temperatura DS18B20',
  category: 'sensores',
  description: 'Termómetro digital 1-Wire de −55 a 125 °C con resolución de 9 a 12 bits. Varios sensores comparten un solo pin (librerías OneWire y DallasTemperature).',
  keywords: 'ds18b20 dallas onewire 1-wire temperatura digital dallastemperature',
  insertable: true,
  props: [
    { key: 'temperature', label: 'Temperatura', type: 'slider', min: -55, max: 125, step: 0.1, unit: '°C', default: 24, runtime: true, live: true },
    { key: 'pullup', label: 'Resistencia pull-up de 4.7 kΩ incluida (como en los módulos)', type: 'boolean', default: false, runtime: true },
  ],
  pins: legs(['GND', 'DQ', 'VDD'], { GND: 'GND', DQ: 'DQ (datos 1-Wire)', VDD: 'VDD (3–5.5 V)' }, { GND: 'gnd', DQ: 'digital', VDD: 'power' }),
  footprint: { w: 7.2, d: 4.6, h: 9.2, z: -0.4 },
  help: 'El bus necesita una resistencia de 4.7 kΩ entre DQ y VDD (una sola para todos los sensores). ' +
    'Con VDD unido a GND funciona en modo parásito. Cada sensor tiene su propia dirección de 64 bits (familia 0x28).',
});
